import React, { useEffect, useMemo, useState } from 'react';
import CustomText from '../../../../Common/customComponents/CustomText.tsx';
import ButtonInnerX from '/assets/icon/global/button-inner-x.svg';
import UserSearchModal from '../../../Setting/modal/UserSearchModal.tsx';
import { SignatureFormProps, SignatureModal } from '../../../../SignatureModal';
import PtwSignatureModal from '../../../MyApproval/modal/PtwSignatureModal.tsx';
import { ptwApi } from '../../../../../api/ptw.api.ts';
import { commonMsg } from '../../../../../utils/globalUtil.tsx';
import { useAlarm, useUser } from '../../../../../hooks';
import { usePermitIssue } from 'containers/ptw/contexts/PermitIssueContext.ts';
import { usePtwDetailModal } from '../../../contexts/PtwDetailModalContext.ts';
import { useStatusUpdateDate } from '../../../contexts/PermitStatusUpdateDate.ts';

interface IProps {
  data: any;
  setData: any;
  type: 'ISSUE' | 'DAILY' | 'CLOSURE' | 'EXTEND';
  approverItem: any;
  isReadOnly: boolean;
  onRefresh?: () => void;
  isFinish: boolean;
}

const FormApprovalRow = (props: IProps) => {
  const { showAlarm, closeAlarm } = useAlarm();
  const user = useUser();
  const {
    isPtwDetailModalOpen,
    setIsPtwDetailModalOpen,
    setSelectedApprovalType,
    selectedApprovalType,
    autoOpenSignatureType,
    setAutoOpenSignatureType,
    saveBeforeApproval
  } = usePtwDetailModal();

  const [isUserSearchModalOpen, setIsUserSearchModalOpen] = useState<boolean>(false);
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [selectedSeq, setSelectedSeq] = useState<number | null>(null);
  const [selectedUserList, setSelectedUserList] = useState<any>([]);

  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState<boolean>(false);

  const [isSignReadonly, setIsSignReadonly] = useState<boolean>(false);
  const { pageId } = usePermitIssue();

  const { statusUpdateDate, setStatusUpdateDate } = useStatusUpdateDate();

  const getApprovalKey = (item: any): number | null => {
    return item?.approvalMSeq ?? item?.settingApprovalModeSeq ?? null;
  };

  const onActionUserSearchCallback = (checkedList: any[]) => {
    if (!selectedSeq) return;

    props.setData((prev: any) => {
      const prevList = prev?.approvalMVOList ?? [];

      const nextList = prevList.map((m: any) => {
        if (getApprovalKey(m) !== selectedSeq) return m;

        const prevUsers: any[] = m?.ptwPageApprovalDVOList ?? [];
        const existIds = new Set(prevUsers.map((u: any) => u?.userId).filter(Boolean));

        const addUsers = (checkedList ?? [])
          .filter((u: any) => u?.userId && !existIds.has(u.userId))
          .map((u: any) => ({
            userId: u.userId,
            userNm: u.userNm
          }));

        return {
          ...m,
          ptwPageApprovalDVOList: [...prevUsers, ...addUsers]
        };
      });

      return { ...prev, approvalMVOList: nextList };
    });

    setSelectedSeq(null);
    setSelectedUserList([]);
  };

  const delUser = (approverItem: any, userId: string) => {
    const targetKey = getApprovalKey(approverItem);
    if (targetKey == null) return;

    props.setData((prev: any) => {
      const prevList: any[] = prev?.approvalMVOList ?? [];

      const nextList = prevList.map((m: any) => {
        if (getApprovalKey(m) !== targetKey) return m;

        const prevUsers: any[] = m?.ptwPageApprovalDVOList ?? [];
        const nextUsers = prevUsers.filter((u: any) => u?.userId !== userId);

        return { ...m, ptwPageApprovalDVOList: nextUsers };
      });

      return { ...prev, approvalMVOList: nextList };
    });
  };

  const onActionSignatureCallback = (
    approvalProcessSeq: number,
    approvalMSeq: number,
    ptwDSeq: number,
    signType: string,
    base64: string,
    comment: string,
    addUserList: any[],
    nextApprovalMSeq: number
  ) => {
    const formData = new FormData();
    formData.set('approvalProcessSeq', String(approvalProcessSeq));
    formData.set('approvalMSeq', String(approvalMSeq));
    formData.set('ptwDSeq', String(ptwDSeq));
    formData.set('signType', signType);
    formData.set('sign', base64);
    formData.set('nextApprovalMSeq', String(nextApprovalMSeq));
    formData.set('approvalType', props.type);

    if (comment != null && comment != '') {
      formData.set('comment', comment);
    }

    for (let i = 0; i < addUserList.length; i++) {
      const row = addUserList[i];
      for (const [k, v] of Object.entries(row)) {
        if (k == 'userId') {
          formData.append(`addUserList[${i}]`, String(v ?? ''));
        }
      }
    }

    ptwApi.setApprovalProcessSign(formData).then(async (r: any) => {
      if (r.resultObject > 0) {
        props.onRefresh?.();
        commonMsg.alert(showAlarm, closeAlarm, 'Success', 'update success', () => {
          setIsPtwDetailModalOpen(false);
        });
        props.setData((prev: any) => {
          const prevList: any[] = prev?.approvalMVOList ?? [];
          const nextList = prevList.map((m: any) => {
            if (m.approvalMSeq !== approvalMSeq) return m;
            return { ...m, signType: signType, sign: base64, comment: comment };
          });
          return { ...prev, approvalMVOList: nextList };
        });
        setStatusUpdateDate(new Date());
      } else {
        commonMsg.alert(showAlarm, closeAlarm, 'Error', 'Error', null);
      }
    });
  };

  const approvalRow = useMemo(() => {
    const key = getApprovalKey(props.approverItem);
    if (key == null) return undefined;

    return (props.data?.approvalMVOList ?? []).find((m: any) => getApprovalKey(m) === key);
  }, [props.data?.approvalMVOList, props.approverItem]);

  const renderSignatureBlock = () => {
    /* my issue, my status, isFinish가 true 인경우 사인 불가 */
    if (pageId == 'issue' || pageId == 'status' || props.isFinish) {
      if (!approvalRow?.signType) {
        if (!approvalRow?.isOnline) {
          return (
            <div className={'sign-div'}>
              <span>Sign on Paper</span>
            </div>
          );
        } else {
          return (
            <div className={'sign-div'}>
              <span>Signature</span>
            </div>
          );
        }
      } else {
        return (
          <div
            className={'sign-div'}
            onClick={() => {
              setIsSignReadonly(true);
              setIsSignatureModalOpen(true);
            }}
          >
            {approvalRow.signType == 'DENY' && (
              <div>
                <span className={'sign-deny'}>REJECT</span>
              </div>
            )}

            {approvalRow.signType == 'APPROVED' && (
              <img
                src={`data:image/png;base64,${approvalRow.sign}`}
                alt="signature"
                style={{ width: '100%', height: 'auto', maxWidth: 240 }}
              />
            )}
          </div>
        );
      }
    } else {
      if (!approvalRow.signType) {
        // 사인 가능(미 사인)
        if (approvalRow.approvalProcessSeq != null) {
          // 결재라인이 생성된 경우
          if (selectedApprovalType == props.approverItem.approvalType) {
            return (
              <div
                className={'sign-div active'}
                onClick={async () => {
                  if (saveBeforeApproval.current) {
                    const ok = await saveBeforeApproval.current();
                    if (!ok) return;
                  }
                  setIsSignReadonly(false);
                  setIsSignatureModalOpen(true);
                }}
              >
                <span>Signature</span>
              </div>
            );
          } else {
            return (
              <div className={'sign-div'}>
                <span>Signature</span>
              </div>
            );
          }
        } else {
          // 결재라인이 없는 경우(초기화면 또는 OFFLINE)
          if (!approvalRow.isOnline) {
            return (
              <div className={'sign-div'}>
                <span>Sign on Paper</span>
              </div>
            );
          } else {
            return (
              <div className={'sign-div'}>
                <span>Signature</span>
              </div>
            );
          }
        }
      } else {
        // 사인 완료
        return (
          <div
            className={'sign-div'}
            onClick={() => {
              setIsSignReadonly(true);
              setIsSignatureModalOpen(true);
            }}
          >
            {approvalRow.signType == 'DENY' && (
              <div>
                <span className={'sign-deny'}>REJECT</span>
              </div>
            )}

            {approvalRow.signType == 'APPROVED' && (
              <img
                src={`data:image/png;base64,${approvalRow.sign}`}
                alt="signature"
                style={{ width: '100%', height: 'auto', maxWidth: 240 }}
              />
            )}
          </div>
        );
      }
    }
  };

  const isChangeApproval = () => {
    let value = true;

    if (!props.approverItem.isOnline) {
      return false;
    } else if (pageId == 'issue') {
      if (props.data?.ptwNo == null) {
        value = true;
      } else {
        value = false;
      }
    } else if (pageId == 'approval') {
      return false;
    } else {
      value = false;
    }
    return value;
  };

  return (
    <>
      <PtwSignatureModal
        setIsModalOpen={setIsSignatureModalOpen}
        isModalOpen={isSignatureModalOpen}
        onActionCallback={onActionSignatureCallback}
        approverItem={props.approverItem}
        isReadonly={isSignReadonly}
      />

      <UserSearchModal
        setIsModalOpen={setIsUserSearchModalOpen}
        isModalOpen={isUserSearchModalOpen}
        title={'Approval Authority'}
        onActionCallback={onActionUserSearchCallback}
        selectedList={selectedUserList}
        searchKeyword={searchKeyword}
        settingApproverMSeq={props?.approverItem?.settingApproverMSeq}
      />
      <tr>
        <th style={{ width: '250px' }}>{props.approverItem.approverCategory}</th>
        <td>
          <div className={'group-td-column'}>
            <div className={'approver-select-list'}>
              {(props.approverItem?.ptwPageApprovalDVOList ?? []).map(
                (userItem: any, userIndex: number) => (
                  <div className={'selected-item'} key={userIndex}>
                    <span>{userItem.userNm}</span>
                    {isChangeApproval() && (
                      <button
                        onClick={() => {
                          delUser(props.approverItem, userItem.userId);
                        }}
                      >
                        <img src={ButtonInnerX} alt={'button inner x'} />
                      </button>
                    )}
                  </div>
                )
              )}
            </div>
            <div className={'group-td-row'}>
              <div style={{ flex: '1', width: '100%' }}>
                <CustomText
                  name={'searchKeyword'}
                  value={searchKeyword}
                  isBtnSearch={isChangeApproval()}
                  onChange={(e: any) => {
                    setSearchKeyword(e.target.value);
                  }}
                  onSearchAction={() => {
                    setSelectedSeq(getApprovalKey(props.approverItem));
                    setSelectedUserList(props.approverItem.ptwPageApprovalDVOList);
                    setIsUserSearchModalOpen(true);
                  }}
                  isBtnClose={true}
                  readOnly={!isChangeApproval()}
                  maxLength={30}
                />
              </div>
            </div>

            {/*사인화면*/}
            {renderSignatureBlock()}

            {/*Site Verification & Review comment*/}
            {props.approverItem?.approverCategory === 'Site Verification & Review' && (
              <div
                style={{
                  height: '50px',
                  backgroundColor: '#F8F8F8',
                  border: '0.5px solid #DADADA',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 12px',
                  gap: '6px',
                  marginTop: '1px'
                }}
              >
                <span
                  style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#585959',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Comment:
                </span>
                <span
                  style={{ fontSize: '14px', color: approvalRow?.comment ? '#333' : '#ADADAD' }}
                >
                  {approvalRow?.comment || '-'}
                </span>
              </div>
            )}
          </div>
        </td>
      </tr>
    </>
  );
};

export default FormApprovalRow;
